import {
    Check,
    Copy, ExternalLink, Eye,
    EyeOff, Key,
    RefreshCw,
    Send,
    TestTube,
    Webhook
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { KgsBankSelect } from '../components/ui/kgs-bank-select';
import { Label } from '../components/ui/label';
import { useMerchantAuth } from '../contexts/MerchantAuthContext';
import { AnalyticsProperties, ANALYTICS_EVENTS, trackAnalyticsEvent } from '../lib/analytics';
import {
    merchantAuthAPI,
    merchantSandboxAPI,
    SandboxTransaction,
    SandboxWebhookLog,
    TypicalMethod
} from '../lib/api';
import { cabinetPath } from '../lib/domains';
import { buildTypicalCommonPayload, filterTypicalMethods, resolveMerchantFiatCurrency, typicalPhonePlaceholder } from '../lib/typicalV2';

const createMerchantId = (prefix: string) =>
    `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const statusOptions = ['assigned', 'receipt_uploaded', 'confirmed', 'completed', 'cancelled', 'disputed'];

const TYPICAL_METHOD_LABELS: Partial<Record<TypicalMethod, string>> = {
    mobile_tjs: 'Mobile Commerce TJS',
    card: 'Карта',
    sbp: 'СБП',
    sbp_kgs: 'Телефон (SBP KGS)',
    kzt_transgran_gel: 'kzt_transgran_gel',
    kzt_transgran_idr: 'kzt_transgran_idr',
    kzt_transgran_tjs: 'kzt_transgran_tjs',
    kzt_transgran_uzs: 'kzt_transgran_uzs',
    rub_transgran_uzs_emission_tbank: 'rub_transgran_uzs_emission_tbank',
    rub_transgran_uzs_emission_vtb: 'rub_transgran_uzs_emission_vtb',
    rub_transgran_uzs_emission_sber: 'rub_transgran_uzs_emission_sber',
    transgran_card_rub_arm: 'transgran_card_rub_arm',
    transgran_mobile_rub_arm: 'transgran_mobile_rub_arm',
    elqr: 'ELQR',
};
const DEFAULT_REDIRECT_URLS = {
    success: 'https://merchant.example.com/success',
    pending: 'https://merchant.example.com/pending',
    fail: 'https://merchant.example.com/fail',
};

const getSandboxAnalyticsProperties = (
    data: any,
    operationType: 'deposit' | 'withdraw',
    merchantId?: string
): AnalyticsProperties => ({
    merchant_id: merchantId,
    operation_type: operationType,
    payment_id: data?.payment_id,
    transaction_id: data?.transaction_id || data?._id,
    status: data?.status,
    fiat_amount: data?.fiat_amount,
    exchange_rate: data?.exchange_rate,
    environment: 'sandbox',
});

export const MerchantSandboxPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, isLoading, refreshUser } = useMerchantAuth();
    const [showSandboxKey, setShowSandboxKey] = useState(false);
    const [showSandboxSecret, setShowSandboxSecret] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [sandboxWebhookUrl, setSandboxWebhookUrl] = useState('');
    const [webhookSaving, setWebhookSaving] = useState(false);
    const [operationType, setOperationType] = useState<'deposit' | 'withdraw'>('deposit');
    const [userId, setUserId] = useState('sandbox_user_123');
    const [amount, setAmount] = useState(1000);
    const [merchantPaymentId, setMerchantPaymentId] = useState(() => createMerchantId('sandbox_pay'));
    const [merchantWithdrawalId, setMerchantWithdrawalId] = useState(() => createMerchantId('sandbox_wd'));
    const [typicalMethod, setTypicalMethod] = useState<TypicalMethod | ''>('');
    const [accountNumber, setAccountNumber] = useState('');
    const [withdrawBankId, setWithdrawBankId] = useState('');
    const [withdrawFirstName, setWithdrawFirstName] = useState('');
    const [withdrawLastName, setWithdrawLastName] = useState('');
    const [callback, setCallback] = useState('');
    const [successUrl, setSuccessUrl] = useState(DEFAULT_REDIRECT_URLS.success);
    const [pendingUrl, setPendingUrl] = useState(DEFAULT_REDIRECT_URLS.pending);
    const [failUrl, setFailUrl] = useState(DEFAULT_REDIRECT_URLS.fail);
    const [createLoading, setCreateLoading] = useState(false);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [logsLoading, setLogsLoading] = useState(false);
    const [simulateLoadingId, setSimulateLoadingId] = useState<string | null>(null);
    const [response, setResponse] = useState<any>(null);
    const [transactions, setTransactions] = useState<SandboxTransaction[]>([]);
    const [logs, setLogs] = useState<SandboxWebhookLog[]>([]);
    const [error, setError] = useState<string | null>(null);

    const sandboxApiKey = user?.sandbox_api_key || '';
    const isTypicalIntegration = user?.integration_profile === 'typical_v2_2';
    const fiatCurrency = resolveMerchantFiatCurrency(user);
    const availableTypicalMethods = useMemo(
        () => filterTypicalMethods(user?.requisite_types || [], fiatCurrency, operationType),
        [fiatCurrency, operationType, user?.requisite_types]
    );
    const sandboxSigningSecret = user?.sandbox_webhook_secret || '';

    const loadSandboxData = useCallback(async () => {
        if (!sandboxApiKey) return;
        setTransactionsLoading(true);
        setLogsLoading(true);
        try {
            const [txResponse, logResponse] = await Promise.all([
                merchantSandboxAPI.getTransactions(sandboxApiKey, 1, 20),
                merchantSandboxAPI.getWebhookLogs(sandboxApiKey, 20),
            ]);
            setTransactions(txResponse.transactions);
            setLogs(logResponse.logs);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Не удалось загрузить sandbox данные');
        } finally {
            setTransactionsLoading(false);
            setLogsLoading(false);
        }
    }, [sandboxApiKey]);

    useEffect(() => {
        if (user) {
            setSandboxWebhookUrl(user.sandbox_webhook_url || '');
            if (user.integration_profile === 'typical_v2_2' && !callback && user.sandbox_webhook_url) {
                setCallback(user.sandbox_webhook_url);
            }
        }
    }, [callback, user]);

    useEffect(() => {
        if (!isTypicalIntegration) return;
        if (!typicalMethod || !availableTypicalMethods.includes(typicalMethod)) {
            setTypicalMethod(availableTypicalMethods[0] || '');
        }
    }, [availableTypicalMethods, isTypicalIntegration, typicalMethod]);

    useEffect(() => {
        if (sandboxApiKey) {
            loadSandboxData();
        }
    }, [sandboxApiKey, loadSandboxData]);

    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Ошибка копирования:', err);
        }
    };

    const handleSaveSandboxWebhook = async () => {
        setWebhookSaving(true);
        setError(null);
        try {
            await merchantAuthAPI.updateWebhook(sandboxWebhookUrl.trim(), 'sandbox');
            await refreshUser();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Не удалось сохранить sandbox webhook URL');
        } finally {
            setWebhookSaving(false);
        }
    };

    const handleCreateSandboxTransaction = async () => {
        if (!sandboxApiKey) {
            setError('Sandbox API key не найден. Обновите страницу или войдите заново.');
            return;
        }
        setCreateLoading(true);
        setError(null);
        setResponse(null);

        try {
            const callbackUrl = callback.trim();
            if (isTypicalIntegration) {
                if (!sandboxSigningSecret) {
                    throw new Error('Sandbox signing secret не настроен');
                }
                if (!typicalMethod) {
                    throw new Error('Для операции не настроен доступный метод');
                }
                if (!callbackUrl) {
                    throw new Error('Укажите обязательный callback_url');
                }
                if (operationType === 'withdraw' && (
                    !accountNumber.trim()
                    || !withdrawBankId.trim()
                    || !withdrawFirstName.trim()
                    || !withdrawLastName.trim()
                )) {
                    throw new Error('Для вывода укажите реквизит, bank_id, имя и фамилию получателя');
                }

                const commonPayload = buildTypicalCommonPayload({
                    orderId: operationType === 'deposit' ? merchantPaymentId : merchantWithdrawalId,
                    amount,
                    currency: fiatCurrency,
                    method: typicalMethod,
                    customerAccountId: userId,
                    callbackUrl,
                });
                const data = operationType === 'deposit'
                    ? await merchantSandboxAPI.createTypicalDeposit(sandboxApiKey, sandboxSigningSecret, {
                        ...commonPayload,
                        success_url: successUrl.trim(),
                        pending_url: pendingUrl.trim(),
                        fail_url: failUrl.trim(),
                    })
                    : await merchantSandboxAPI.createTypicalWithdrawal(sandboxApiKey, sandboxSigningSecret, {
                        ...commonPayload,
                        method: typicalMethod as Exclude<TypicalMethod, 'elqr'>,
                        account_number: accountNumber.trim(),
                        fields: {
                            bank_id: withdrawBankId.trim(),
                            first_name: withdrawFirstName.trim(),
                            last_name: withdrawLastName.trim(),
                        },
                    });

                setResponse(data);
                trackAnalyticsEvent(ANALYTICS_EVENTS.SANDBOX_TRANSACTION_CREATED, {
                    ...getSandboxAnalyticsProperties(data, operationType, user?.id),
                    customer_id: userId,
                    request_amount: amount,
                    requisite_type: typicalMethod,
                    has_callback: true,
                });
                if (operationType === 'deposit') {
                    setMerchantPaymentId(createMerchantId('sandbox_pay'));
                } else {
                    setMerchantWithdrawalId(createMerchantId('sandbox_wd'));
                }
                await loadSandboxData();
                return;
            }

            const payload: any = {
                user_id: userId,
                amount,
            };
            if (callbackUrl) {
                payload.callback = callbackUrl;
            }

            const data = operationType === 'deposit'
                ? await merchantSandboxAPI.createPayment(sandboxApiKey, {
                    ...payload,
                    merchantPaymentId: merchantPaymentId,
                })
                : await merchantSandboxAPI.createWithdrawal(sandboxApiKey, {
                    ...payload,
                    merchantWithdrawalId: merchantWithdrawalId,
                });

            setResponse(data);
            trackAnalyticsEvent(ANALYTICS_EVENTS.SANDBOX_TRANSACTION_CREATED, {
                ...getSandboxAnalyticsProperties(data, operationType, user?.id),
                customer_id: userId,
                request_amount: amount,
                has_callback: Boolean(callbackUrl),
            });
            if (operationType === 'deposit') {
                setMerchantPaymentId(createMerchantId('sandbox_pay'));
            } else {
                setMerchantWithdrawalId(createMerchantId('sandbox_wd'));
            }
            await loadSandboxData();
        } catch (err: any) {
            const message = err.response?.data?.detail
                || err.response?.data?.error?.message
                || err.message
                || 'Не удалось создать sandbox транзакцию';
            trackAnalyticsEvent(ANALYTICS_EVENTS.SANDBOX_TRANSACTION_CREATE_FAILED, {
                merchant_id: user?.id,
                operation_type: operationType,
                customer_id: userId,
                request_amount: amount,
                has_callback: Boolean(callback.trim()),
                error_message: message,
                environment: 'sandbox',
            });
            setError(message);
        } finally {
            setCreateLoading(false);
        }
    };

    const handleSimulateStatus = async (transactionId: string, status: string) => {
        if (!sandboxApiKey) return;
        setSimulateLoadingId(`${transactionId}:${status}`);
        setError(null);
        try {
            await merchantSandboxAPI.simulateStatus(sandboxApiKey, transactionId, {
                status,
                sendWebhook: true,
            });
            trackAnalyticsEvent(ANALYTICS_EVENTS.SANDBOX_STATUS_SIMULATED, {
                merchant_id: user?.id,
                transaction_id: transactionId,
                target_status: status,
                environment: 'sandbox',
            });
            await loadSandboxData();
        } catch (err: any) {
            const message = err.response?.data?.detail || 'Не удалось симулировать статус';
            trackAnalyticsEvent(ANALYTICS_EVENTS.SANDBOX_STATUS_SIMULATE_FAILED, {
                merchant_id: user?.id,
                transaction_id: transactionId,
                target_status: status,
                error_message: message,
                environment: 'sandbox',
            });
            setError(message);
        } finally {
            setSimulateLoadingId(null);
        }
    };

    const typicalFieldsComplete = Boolean(
        typicalMethod
        && sandboxSigningSecret
        && callback.trim()
        && (operationType === 'withdraw'
            ? accountNumber.trim()
                && withdrawBankId.trim()
                && withdrawFirstName.trim()
                && withdrawLastName.trim()
            : successUrl.trim() && pendingUrl.trim() && failUrl.trim())
    );
    const canCreateTransaction = Boolean(
        sandboxApiKey
        && amount > 0
        && (!isTypicalIntegration || typicalFieldsComplete)
    );

    if (isLoading) {
        return (
            <div className="min-h-64 flex items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-emerald-600 dark:text-emerald-300" />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-64 flex items-center justify-center">
                <Button onClick={() => navigate(cabinetPath('merchant', '/login'))}>Войти</Button>
            </div>
        );
    }

    return (
        <div className="min-w-0">
            <div className="max-w-6xl mx-auto space-y-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <div className="flex items-center gap-3">
                            <TestTube className="h-7 w-7 text-emerald-600 dark:text-emerald-300" />
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Тестовая среда</h2>
                            <Badge variant="secondary">Sandbox</Badge>
                        </div>
                        <p className="text-gray-600 dark:text-slate-300 mt-2">
                            Тестовые платежи живут в отдельной коллекции и не затрагивают реальные ордера, трейдеров и балансы.
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center justify-end gap-2">
                        <Button variant="outline" onClick={() => navigate(cabinetPath('merchant', '/integration'))}>
                            К интеграции
                        </Button>
                    </div>
                </div>

                {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Key className="h-5 w-5 text-emerald-600 dark:text-emerald-300" />
                                Sandbox доступ
                            </CardTitle>
                            <CardDescription>
                                Используйте этот ключ только для тестовых API-запросов.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Sandbox API key</Label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Input
                                            type={showSandboxKey ? 'text' : 'password'}
                                            value={sandboxApiKey}
                                            readOnly
                                            className="font-mono text-sm pr-10"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                                            onClick={() => setShowSandboxKey(!showSandboxKey)}
                                        >
                                            {showSandboxKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => copyToClipboard(sandboxApiKey, 'sandbox_api_key')}
                                    >
                                        {copiedField === 'sandbox_api_key' ? <Check className="h-4 w-4 text-green-600 dark:text-green-300" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="sandbox_webhook_url">Sandbox webhook URL</Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="sandbox_webhook_url"
                                        type="url"
                                        value={sandboxWebhookUrl}
                                        onChange={(e) => setSandboxWebhookUrl(e.target.value)}
                                        placeholder="https://your-site.com/sandbox-webhook"
                                        className="flex-1"
                                    />
                                    <Button
                                        onClick={handleSaveSandboxWebhook}
                                        disabled={webhookSaving}
                                        className="bg-emerald-600 hover:bg-emerald-700"
                                    >
                                        {webhookSaving ? 'Сохраняем...' : 'Сохранить'}
                                    </Button>
                                </div>
                            </div>

                            {user.sandbox_webhook_secret && (
                                <div className="space-y-2">
                                    <Label>
                                        {isTypicalIntegration ? 'Sandbox signing secret' : 'Sandbox webhook secret'}
                                    </Label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Input
                                                type={showSandboxSecret ? 'text' : 'password'}
                                                value={user.sandbox_webhook_secret}
                                                readOnly
                                                className="font-mono text-sm pr-10"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                                                onClick={() => setShowSandboxSecret(!showSandboxSecret)}
                                            >
                                                {showSandboxSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => copyToClipboard(user.sandbox_webhook_secret || '', 'sandbox_webhook_secret')}
                                        >
                                            {copiedField === 'sandbox_webhook_secret' ? <Check className="h-4 w-4 text-green-600 dark:text-green-300" /> : <Copy className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                    {isTypicalIntegration && (
                                        <p className="text-xs text-gray-500 dark:text-slate-400">
                                            Используется для x-signature запросов v2.2 и проверки sandbox callback.
                                        </p>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Send className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                                Создать sandbox транзакцию
                            </CardTitle>
                            <CardDescription>
                                {isTypicalIntegration
                                    ? 'Запрос уйдёт в Merchant API v2.2 с sandbox-ключом и HMAC-подписью.'
                                    : 'Запрос уйдет в обычный Merchant API, но с sandbox token.'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <Button
                                    variant={operationType === 'deposit' ? 'default' : 'outline'}
                                    onClick={() => setOperationType('deposit')}
                                >
                                    Deposit
                                </Button>
                                <Button
                                    variant={operationType === 'withdraw' ? 'default' : 'outline'}
                                    onClick={() => setOperationType('withdraw')}
                                >
                                    Withdraw
                                </Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="sandbox_user_id">
                                        {isTypicalIntegration ? 'Customer account ID' : 'User ID'}
                                    </Label>
                                    <Input
                                        id="sandbox_user_id"
                                        value={userId}
                                        onChange={(e) => setUserId(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="sandbox_amount">
                                        Сумма {isTypicalIntegration ? fiatCurrency : 'RUB'}
                                    </Label>
                                    <Input
                                        id="sandbox_amount"
                                        type="number"
                                        min="1"
                                        value={amount}
                                        onChange={(e) => setAmount(Number(e.target.value))}
                                    />
                                </div>
                            </div>

                            {isTypicalIntegration && (
                                <div className="space-y-2">
                                    <Label htmlFor="sandbox_method">Метод</Label>
                                    <select
                                        id="sandbox_method"
                                        value={typicalMethod}
                                        onChange={(event) => setTypicalMethod(event.target.value as TypicalMethod)}
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                        disabled={availableTypicalMethods.length === 0}
                                    >
                                        {availableTypicalMethods.length === 0 && (
                                            <option value="">Нет доступных методов</option>
                                        )}
                                        {availableTypicalMethods.map((method) => (
                                            <option key={method} value={method}>
                                                {(!TYPICAL_METHOD_LABELS[method] || TYPICAL_METHOD_LABELS[method] === method)
                                                    ? method
                                                    : `${TYPICAL_METHOD_LABELS[method]} (${method})`}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 dark:text-slate-400">
                                        Показаны только методы, включённые для мерчанта и доступные для выбранной операции.
                                    </p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label>
                                    {isTypicalIntegration
                                        ? 'Order ID'
                                        : operationType === 'deposit' ? 'Merchant Payment ID' : 'Merchant Withdrawal ID'}
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={operationType === 'deposit' ? merchantPaymentId : merchantWithdrawalId}
                                        onChange={(e) => {
                                            if (operationType === 'deposit') {
                                                setMerchantPaymentId(e.target.value);
                                            } else {
                                                setMerchantWithdrawalId(e.target.value);
                                            }
                                        }}
                                        className="font-mono"
                                    />
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            if (operationType === 'deposit') {
                                                setMerchantPaymentId(createMerchantId('sandbox_pay'));
                                            } else {
                                                setMerchantWithdrawalId(createMerchantId('sandbox_wd'));
                                            }
                                        }}
                                    >
                                        Новый
                                    </Button>
                                </div>
                            </div>

                            {isTypicalIntegration && operationType === 'withdraw' && (
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="sandbox_account_number">
                                            {['sbp', 'sbp_kgs'].includes(typicalMethod) ? 'Номер телефона получателя *' : 'Номер карты получателя *'}
                                        </Label>
                                        <Input
                                            id="sandbox_account_number"
                                            value={accountNumber}
                                            onChange={(event) => setAccountNumber(event.target.value)}
                                            placeholder={['sbp', 'sbp_kgs'].includes(typicalMethod) ? typicalPhonePlaceholder(fiatCurrency) : '4111111111111111'}
                                        />
                                    </div>
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="sandbox_withdraw_bank_id">ID банка *</Label>
                                        {fiatCurrency === 'KGS' ? (
                                            <KgsBankSelect
                                                id="sandbox_withdraw_bank_id"
                                                value={withdrawBankId}
                                                onChange={setWithdrawBankId}
                                                disabled={createLoading}
                                            />
                                        ) : (
                                            <Input
                                            id="sandbox_withdraw_bank_id"
                                            value={withdrawBankId}
                                            onChange={(event) => setWithdrawBankId(event.target.value)}
                                            placeholder="mbank"
                                        />
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="sandbox_withdraw_first_name">Имя получателя *</Label>
                                        <Input
                                            id="sandbox_withdraw_first_name"
                                            value={withdrawFirstName}
                                            onChange={(event) => setWithdrawFirstName(event.target.value)}
                                            placeholder="Айбек"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="sandbox_withdraw_last_name">Фамилия получателя *</Label>
                                        <Input
                                            id="sandbox_withdraw_last_name"
                                            value={withdrawLastName}
                                            onChange={(event) => setWithdrawLastName(event.target.value)}
                                            placeholder="Асанов"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="sandbox_callback">
                                    {isTypicalIntegration ? 'Callback URL *' : 'Callback для этой транзакции'}
                                </Label>
                                <Input
                                    id="sandbox_callback"
                                    type="url"
                                    value={callback}
                                    onChange={(e) => setCallback(e.target.value)}
                                    placeholder="https://your-site.com/sandbox-callback"
                                />
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    {isTypicalIntegration
                                        ? 'Обязательное поле v2.2. Финальный callback будет подписан sandbox secret.'
                                        : 'Если заполнить, webhook пойдет сюда, а не на общий sandbox webhook URL.'}
                                </p>
                            </div>

                            {isTypicalIntegration && operationType === 'deposit' && (
                                <div className="grid grid-cols-1 gap-3">
                                    <div className="space-y-2">
                                        <Label htmlFor="sandbox_success_url">Success URL *</Label>
                                        <Input id="sandbox_success_url" type="url" value={successUrl} onChange={(event) => setSuccessUrl(event.target.value)} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="sandbox_pending_url">Pending URL *</Label>
                                        <Input id="sandbox_pending_url" type="url" value={pendingUrl} onChange={(event) => setPendingUrl(event.target.value)} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="sandbox_fail_url">Fail URL *</Label>
                                        <Input id="sandbox_fail_url" type="url" value={failUrl} onChange={(event) => setFailUrl(event.target.value)} />
                                    </div>
                                </div>
                            )}

                            <Button
                                onClick={handleCreateSandboxTransaction}
                                disabled={createLoading || !canCreateTransaction}
                                className="w-full"
                            >
                                {createLoading ? 'Создаем...' : 'Создать sandbox транзакцию'}
                            </Button>

                            {response && isTypicalIntegration && (
                                <div className="p-4 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-md text-sm space-y-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-emerald-700 dark:text-emerald-300">transaction_id</span>
                                        <span className="font-mono text-emerald-900 dark:text-emerald-200 break-all">{response.transaction_id}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <span className="text-emerald-700 dark:text-emerald-300">amount_usdt</span>
                                            <p className="font-semibold">{response.amount_usdt} USDT</p>
                                        </div>
                                        <div>
                                            <span className="text-emerald-700 dark:text-emerald-300">balance_delta</span>
                                            <p className="font-semibold">{response.balance_delta} USDT</p>
                                        </div>
                                    </div>
                                    {response.redirect_url && (
                                        <Button asChild className="w-full">
                                            <a href={response.redirect_url} target="_blank" rel="noopener noreferrer">
                                                <ExternalLink className="mr-2 h-4 w-4" />
                                                Открыть sandbox-платёжку
                                            </a>
                                        </Button>
                                    )}
                                </div>
                            )}

                            {response && !isTypicalIntegration && (
                                <div className="p-4 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-md text-sm space-y-2">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-emerald-700 dark:text-emerald-300">transaction_id</span>
                                        <span className="font-mono text-emerald-900 dark:text-emerald-200 break-all">{response.transaction_id}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <span className="text-emerald-700 dark:text-emerald-300">Статус</span>
                                            <p className="font-semibold">{response.status}</p>
                                        </div>
                                        <div>
                                            <span className="text-emerald-700 dark:text-emerald-300">Fiat amount</span>
                                            <p className="font-semibold">{response.fiat_amount} RUB</p>
                                        </div>
                                        <div>
                                            <span className="text-emerald-700 dark:text-emerald-300">Курс</span>
                                            <p className="font-semibold">{response.exchange_rate}</p>
                                        </div>
                                        <div>
                                            <span className="text-emerald-700 dark:text-emerald-300">USDT net</span>
                                            <p className="font-semibold">{response.merchant_credited_usdt ?? '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <CardTitle>Sandbox транзакции</CardTitle>
                                <CardDescription>Последние тестовые платежи и выплаты.</CardDescription>
                            </div>
                            <Button variant="outline" onClick={loadSandboxData} disabled={transactionsLoading || logsLoading}>
                                <RefreshCw className={`h-4 w-4 mr-2 ${transactionsLoading ? 'animate-spin' : ''}`} />
                                Обновить
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {transactions.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-slate-400">Sandbox транзакций пока нет.</p>
                        ) : (
                            <div className="space-y-3">
                                {transactions.map((transaction) => (
                                    <div key={transaction._id} className="border border-gray-200 dark:border-slate-700 rounded-md p-4 space-y-3 bg-white dark:bg-slate-800">
                                        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Badge variant="outline">{transaction.order_type}</Badge>
                                                    <Badge variant="secondary">{transaction.status}</Badge>
                                                    <span className="font-mono text-sm text-gray-700 dark:text-slate-200 break-all">{transaction._id}</span>
                                                </div>
                                                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                                                    {transaction.user_id} · {transaction.fiat_amount} {transaction.fiat_currency || (isTypicalIntegration ? fiatCurrency : 'RUB')} · {transaction.merchant_payment_id || transaction.merchant_withdrawal_id}
                                                </p>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {statusOptions.map((status) => (
                                                    <Button
                                                        key={status}
                                                        variant={transaction.status === status ? 'secondary' : 'outline'}
                                                        size="sm"
                                                        onClick={() => handleSimulateStatus(transaction._id, status)}
                                                        disabled={simulateLoadingId === `${transaction._id}:${status}`}
                                                    >
                                                        {simulateLoadingId === `${transaction._id}:${status}` ? '...' : status}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                        {transaction.callback_url && (
                                            <p className="text-xs text-gray-500 dark:text-slate-400 break-all">
                                                callback: {transaction.callback_url}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Webhook className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                            Лог sandbox webhook-ов
                        </CardTitle>
                        <CardDescription>
                            Последние попытки доставки, включая callback override.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {logs.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-slate-400">Webhook-логов пока нет.</p>
                        ) : (
                            <div className="space-y-3">
                                {logs.map((log) => (
                                    <div key={log._id} className="border border-gray-200 dark:border-slate-700 rounded-md p-4 bg-white dark:bg-slate-800">
                                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Badge variant={log.delivered ? 'success' : 'destructive'}>
                                                        {log.delivered ? 'delivered' : 'failed'}
                                                    </Badge>
                                                    <span className="text-sm text-gray-600 dark:text-slate-300">
                                                        HTTP {log.http_status ?? '-'}
                                                    </span>
                                                    <span className="text-sm text-gray-600 dark:text-slate-300">{log.status}</span>
                                                </div>
                                                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 break-all">
                                                    {log.webhook_url || 'Webhook URL не настроен'}
                                                </p>
                                            </div>
                                            <span className="text-xs text-gray-500 dark:text-slate-400">{log.created_at}</span>
                                        </div>
                                        {log.error && (
                                            <p className="text-sm text-red-600 dark:text-red-300 mt-2">{log.error}</p>
                                        )}
                                        {log.payload && (
                                            <pre className="mt-3 p-3 bg-gray-50 dark:bg-slate-900 rounded text-xs overflow-auto max-h-56">
                                                {JSON.stringify(log.payload, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
